import { useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { SAMPLE_INVESTOR_ID } from '@dealstack/shared/lib/documents/fieldBindingCatalogue';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { getInvestorsNamesFiltering } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import type { InvestorOption } from '@dealstack/frontend/shared/components/FieldBinding/fieldBinding.types.ts';

// Lazily-loaded investor list for the "Preview as" picker — fetched once the
// dropdown is first opened. Not searchable: the picker just lists every
// investor to select from. Returns the option list (sample investor always
// first, so it resolves before the lazy list loads), a `onDropdownOpen` to
// trigger the fetch, and the in-flight flag for the nothing-found message.
// `enabled` gates the underlying query — pass false where there is no deal
// (and therefore no investors), while still calling this hook unconditionally.
// `preload` fetches without waiting for the dropdown: the ←/→ shortcuts step
// through this list, which until then holds only the sample investor.
export const useInvestorOptions = ({
	enabled,
	preload = false,
}: {
	enabled: boolean;
	preload?: boolean;
}): {
	options: InvestorOption[];
	onDropdownOpen: () => void;
	isFetching: boolean;
} => {
	const tMipManage = useTranslationMipManage();
	const [investorFilterEnabled, setInvestorFilterEnabled] = useState(false);

	const investorNamesQuery = useInfiniteQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_NAMES_FILTERING_GET],
		initialPageParam: '',
		queryFn: async ({ pageParam }) =>
			await getInvestorsNamesFiltering(API_PATHS.MIP_INVESTOR_NAMES_FILTERING_GET, {
				cursor: pageParam,
				limit: 100,
			}),
		getNextPageParam: (lastPage) => lastPage.payload?.nextCursor,
		enabled: (investorFilterEnabled || preload) && enabled,
	});

	// The sample investor is always offered first (and is the default), so its
	// label resolves in the Select even before the lazy investor list loads.
	const options = useMemo<InvestorOption[]>(
		() => [
			{
				value: SAMPLE_INVESTOR_ID,
				label: tMipManage('master_documents.field_binding.investor_select.sample_option'),
			},
			...(investorNamesQuery.data?.pages?.flatMap(
				(page) =>
					page?.payload?.investors.map(({ id, person }) => ({
						value: id,
						label: person.displayName,
					})) ?? []
			) ?? []),
		],
		[investorNamesQuery.data, tMipManage]
	);

	return {
		options,
		onDropdownOpen: () => setInvestorFilterEnabled(true),
		isFetching: investorNamesQuery.isFetching,
	};
};
